import * as React from "react";
import {
  Grid,
  GridColumn as Column,
  GridColumnMenuFilter,
  GridColumnMenuCheckboxFilter,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { TextArea } from "@progress/kendo-react-inputs";
import { process } from "@progress/kendo-data-query";
import UserContext from "../../layouts/UserContext";
import ErrorBanner from "../../components/ErrorBanner";
import {
  useQueryGetCarrierReadinessList,
  useQueryGetCarrierReadinessAudit,
  useMutatePutCarrierReadiness,
} from "../../data/services/carrierService";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const CarrierReadinessTab = () => {
  const { loggedInUser } = React.useContext(UserContext);
  const isAdmin = Boolean(loggedInUser?.isAdmin);
  const [gridDataState, setGridDataState] = React.useState({ take: 20, skip: 0 });
  const [showEditDialog, setShowEditDialog] = React.useState(false);
  const [showAuditDialog, setShowAuditDialog] = React.useState(false);
  const [selectedCarrier, setSelectedCarrier] = React.useState(null);
  const [nextReadyState, setNextReadyState] = React.useState(true);
  const [reason, setReason] = React.useState("");
  const [reasonError, setReasonError] = React.useState(false);

  const { isLoading, data } = useQueryGetCarrierReadinessList();
  const mutateReadiness = useMutatePutCarrierReadiness();

  const {
    isLoading: auditLoading,
    data: auditData,
  } = useQueryGetCarrierReadinessAudit(selectedCarrier?.carrierId, showAuditDialog && !!selectedCarrier?.carrierId);

  const masterList = React.useMemo(
    () => (!isLoading && data?.data ? data.data.slice(0) : []),
    [isLoading, data]
  );

  const listSrc = React.useMemo(
    () => process(masterList, gridDataState),
    [masterList, gridDataState]
  );

  const pagerSetting = {
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: [10, 20, 50, 100, 200, 500],
    previousNext: true,
  };

  const columnMenuHeader = (field) => {
    const active = GridColumnMenuFilter.active(field, gridDataState.filter);
    return active ? "active" : "";
  };

  const columnMenu = (props) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} hideSecondFilter={true} />
      </div>
    );
  };

  const checkboxMenu = (props) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter {...props} data={masterList} expanded={true} />
      </div>
    );
  };

  const statusCell = (props) => {
    const isReady = props.dataItem.isReady;
    const bg = isReady ? "#2e7d32" : "#c62828";
    const label = isReady ? "Ready" : "Not Ready";

    return (
      <td>
        <span
          style={{
            padding: "2px 10px",
            borderRadius: 12,
            color: "#fff",
            backgroundColor: bg,
            fontSize: 12,
          }}
        >
          {label}
        </span>
      </td>
    );
  };

  const dateCell = (props) => {
    return <td>{formatDate(props.dataItem.lastUpdatedAt)}</td>;
  };

  const actionCell = (props) => {
    const item = props.dataItem;

    return (
      <td>
        {isAdmin ? (
          <Button
            themeColor={item.isReady ? "error" : "primary"}
            onClick={() => {
              setSelectedCarrier(item);
              setNextReadyState(!item.isReady);
              setReason("");
              setReasonError(false);
              setShowEditDialog(true);
            }}
          >
            {item.isReady ? "Set Not Ready" : "Set Ready"}
          </Button>
        ) : (
          <span style={{ color: "#64748b" }}>Read-only</span>
        )}
        <Button
          themeColor="light"
          style={{ marginLeft: 10 }}
          onClick={() => {
            setSelectedCarrier(item);
            setShowAuditDialog(true);
          }}
        >
          View Audit
        </Button>
      </td>
    );
  };

  const onDataStateChange = (event) => {
    setGridDataState(event.dataState);
  };

  const onSaveReadiness = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError(true);
      return;
    }

    await mutateReadiness.mutateAsync({
      carrierId: selectedCarrier.carrierId,
      isReady: nextReadyState,
      reason: trimmedReason,
    });

    setShowEditDialog(false);
  };

  return (
    <div>
      <h2 style={{ margin: "10px", marginBottom: 20 }}>Carrier Readiness</h2>
      <p style={{ marginTop: 0, marginBottom: 16 }}>
        Manage global carrier readiness. Changes apply immediately and are audited.
      </p>
      {!isAdmin ? (
        <p style={{ marginTop: 0, marginBottom: 16, color: "#b45309", fontWeight: 600 }}>
          You have read-only access. Only administrators can update carrier readiness status.
        </p>
      ) : null}
      <ErrorBanner
        handler={mutateReadiness}
        onError={() => setShowEditDialog(false)}
      />
      <Grid
        sortable={true}
        pageable={pagerSetting}
        pageSize={20}
        data={listSrc}
        {...gridDataState}
        onDataStateChange={onDataStateChange}
      >
        <Column
          field="carrierName"
          title="Carrier"
          filter="text"
          columnMenu={checkboxMenu}
          headerClassName={columnMenuHeader("carrierName")}
        />
        <Column field="isReady" title="Status" cell={statusCell} sortable={false} />
        <Column
          field="lastReason"
          title="Last Reason"
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnMenuHeader("lastReason")}
        />
        <Column
          field="lastUpdatedBy"
          title="Last Updated By"
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnMenuHeader("lastUpdatedBy")}
        />
        <Column field="lastUpdatedAt" title="Last Updated At" cell={dateCell} />
        <Column title="Action" cell={actionCell} sortable={false} />
      </Grid>

      {showEditDialog && selectedCarrier && (
        <Dialog
          title={`Update Readiness - ${selectedCarrier.carrierName}`}
          onClose={() => setShowEditDialog(false)}
          width={640}
        >
          <div style={{ marginBottom: 16 }}>
            <strong>New Status: </strong>
            <span>{nextReadyState ? "Ready" : "Not Ready"}</span>
          </div>
          <div>
            <label htmlFor="carrier-readiness-reason"><strong>Reason (required)</strong></label>
            <TextArea
              id="carrier-readiness-reason"
              style={{ width: "100%", marginTop: 8 }}
              rows={4}
              value={reason}
              maxLength={2000}
              onChange={(e) => {
                setReason(e.value);
                if (reasonError && e.value.trim()) {
                  setReasonError(false);
                }
              }}
            />
            {reasonError && (
              <div style={{ color: "#c62828", marginTop: 6 }}>
                Reason is required.
              </div>
            )}
          </div>
          <DialogActionsBar>
            <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button themeColor="primary" onClick={onSaveReadiness}>
              Save
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {showAuditDialog && selectedCarrier && (
        <Dialog
          title={`Readiness Audit - ${selectedCarrier.carrierName}`}
          onClose={() => setShowAuditDialog(false)}
          width={900}
        >
          {auditLoading ? (
            <div>Loading...</div>
          ) : (
            <Grid data={auditData?.data || []} sortable={true}>
              <Column field="oldIsReady" title="From" cell={(props) => <td>{props.dataItem.oldIsReady ? "Ready" : "Not Ready"}</td>} />
              <Column field="newIsReady" title="To" cell={(props) => <td>{props.dataItem.newIsReady ? "Ready" : "Not Ready"}</td>} />
              <Column field="reason" title="Reason" />
              <Column field="createdBy" title="Changed By" />
              <Column field="dateCreated" title="Changed At" cell={(props) => <td>{formatDate(props.dataItem.dateCreated)}</td>} />
            </Grid>
          )}
          <DialogActionsBar>
            <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default CarrierReadinessTab;
